import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAcceptInvitation, useInviteLookup, useMe } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import {
  Alert,
  Button,
  Field,
  PasswordField,
  PasswordRules,
  Spinner,
  isPasswordValid,
} from '../components/ui.js';
import { AuthShell } from './LoginPage.js';

export function AcceptInvitePage() {
  const { data: user } = useMe();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const lookup = useInviteLookup(token);
  const accept = useAcceptInvitation();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  if (user) return <Navigate to="/" replace />;

  if (!token) {
    return (
      <AuthShell title="Invitation">
        <Alert>This link is missing its invitation token. Ask for a fresh invite.</Alert>
      </AuthShell>
    );
  }

  if (lookup.isPending) {
    return (
      <AuthShell title="Invitation">
        <Spinner label="Checking your invitation…" />
      </AuthShell>
    );
  }

  // A null result (or error) means the token is unknown, expired, or used.
  if (lookup.isError || !lookup.data) {
    return (
      <AuthShell title="Invitation">
        <Alert>
          This invitation is invalid, expired, or already used. Ask a super admin to send a new one.
        </Alert>
      </AuthShell>
    );
  }

  const invitation = lookup.data;
  const isFounder = invitation.role !== 'developer';
  const mismatch = confirm.length > 0 && password !== confirm;
  const namesGiven = firstName.trim().length > 0 && lastName.trim().length > 0;
  const projectGiven = !isFounder || projectName.trim().length > 0;
  const canSubmit =
    isPasswordValid(password) &&
    password === confirm &&
    namesGiven &&
    projectGiven &&
    !accept.isPending;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!canSubmit) return;
    accept.mutate(
      {
        token,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        projectName: isFounder ? projectName.trim() : undefined,
      },
      { onSuccess: () => void navigate('/', { replace: true }) },
    );
  };

  const error =
    accept.error instanceof ApiError && accept.error.code === 'invalid_invitation'
      ? 'This invitation is no longer valid. Ask for a new one.'
      : accept.isError
        ? 'Something went wrong. Please try again.'
        : null;

  return (
    <AuthShell title="Set your password">
      <p className="mb-4 text-sm opacity-70">
        You’ve been invited to Aldemis Foundry as <strong>{invitation.email}</strong> (
        {invitation.role === 'developer' ? 'developer' : 'founder'}). Choose a password to finish
        setting up your account.
      </p>
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        {error ? <Alert>{error}</Alert> : null}
        <Field
          label="First name"
          autoComplete="given-name"
          required
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Field
          label="Last name"
          autoComplete="family-name"
          required
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        {isFounder ? (
          <Field
            label="Project name"
            placeholder="What are we building?"
            required
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
        ) : null}
        <PasswordField
          label="Password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordRules value={password} />
        <PasswordField
          label="Confirm password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch ? 'The passwords do not match.' : undefined}
        />
        <Button type="submit" disabled={!canSubmit}>
          {accept.isPending ? 'Setting up…' : 'Create account'}
        </Button>
      </form>
    </AuthShell>
  );
}
